import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";

/* Global Types & Enums */
import { TeamStatus } from "@coworker/apprestructured/src/shared/types/team";

/* Global Hooks */
import { useTranslation } from "@coworker/locales";
import { useTeamsByStore } from "@coworker/app/src/core/hooks/useStoreTeams";
import { useActiveStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useActiveStoreUsers";

/* Teams Management Hooks */
import useFilteredUsers from "../hooks/useFilteredUsers";
import useTeamManagerFilters from "../hooks/filters/useTeamManagerFilters";

/* Global Components */
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { FilterPanel } from "@coworker/apprestructured/src/shared/filters/components/FilterPanel";

/* Teams Management Components */
import TeamUsersTable from "./Tables/TeamUsersTable";
import EditTeamModal from "./TeamModal/EditTeamModal";
import AddUserModal from "./AddUserModal";
import useHasTeamEditorPermissions from "../hooks/useTeamEditorPermissions";

const Container = styled.div`
  padding: 32px 64px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  @media (max-width: 768px) {
    padding: 16px 8px;
  }
`;

const ActionsSection = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const SearchAndButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const TeamManager = () => {
  const { t } = useTranslation();
  const { storeId, teamId } = useParams();
  const teamAdminLink = useMemo(
    () => `/teams/store/${encodeURIComponent(storeId || "")}`,
    [storeId]
  );
  const navigate = useNavigate();

  const [usersSearchQuery, setUsersSearchQuery] = useState("");
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [editTeamModalOpen, setEditTeamModalOpen] = useState(false);

  const {
    data: teams,
    isLoading: isLoadingTeams,
    isFetching: isFetchingTeams,
  } = useTeamsByStore(storeId || "");
  const team = useMemo(() => {
    return teams?.find((team) => team.id === teamId) || undefined;
  }, [teams, teamId, isLoadingTeams, isFetchingTeams]);

  const hasActiveProductQualityTeam = useMemo(() => {
    if (isLoadingTeams || isFetchingTeams || !teams) return false;
    return (
      teams.filter(
        (team) => team.is_product_quality && team.status === TeamStatus.ACTIVE
      ).length > 0
    );
  }, [teams, isLoadingTeams, isFetchingTeams]);

  const {
    data: allUsers,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
  } = useActiveStoreUsers(storeId || "");
  const teamUsers = useMemo(() => {
    if (!allUsers || !team) return [];
    return allUsers.filter((user) => user.teamId === team.id);
  }, [allUsers, team, isLoadingUsers, isFetchingUsers, isFetchingTeams]);

  const { filterValues, setFilterValues, useFiltersFactory, resetFilters } =
    useTeamManagerFilters(teamUsers || [], teamId || "");

  const filteredUsers = useFilteredUsers(
    teamUsers || [],
    [],
    filterValues,
    isFetchingUsers || isLoadingUsers,
    teamId,
    usersSearchQuery
  );

  const isUserAuthorized = useHasTeamEditorPermissions(storeId || "");
  if (!isUserAuthorized) return null;

  return (
    <div>
      <Linkbar
        loading={isFetchingTeams || isLoadingTeams}
        items={[
          {
            label: t("teamManagementString"),
            to: teamAdminLink,
          },
        ]}
        currentLabel={team?.name || ""}
        currentAsH1
      />
      <Container>
        <ActionsSection>
          <div>
            <FilterPanel
              filterValues={filterValues}
              setFilterValues={setFilterValues}
              useFiltersFactory={useFiltersFactory}
              feature="teamManager"
            />
          </div>
          <div>
            <SearchAndButtonsWrapper>
              <FixaSearch
                id="search"
                onClear={() => setUsersSearchQuery("")}
                placeholder={t("searchUsersString")}
                value={usersSearchQuery}
                onChange={(e) => setUsersSearchQuery(e.currentTarget.value)}
              />
              <FixaButton
                onClick={() => setAddUserModalOpen(true)}
                text={t("addUserString")}
                size="medium"
                type="primary"
              />
              <FixaButton
                onClick={() => setEditTeamModalOpen(true)}
                text={t("editTeamString")}
                size="medium"
                type="secondary"
              />
            </SearchAndButtonsWrapper>
          </div>
        </ActionsSection>
        <TeamUsersTable
          users={filteredUsers || []}
          selectedTeam={team}
          isLoading={isFetchingUsers || isLoadingUsers}
          resetFilters={resetFilters}
        />
      </Container>
      {storeId && team && (
        <>
          <AddUserModal
            visible={addUserModalOpen}
            team={team}
            close={() => setAddUserModalOpen(false)}
            users={allUsers}
            storeId={storeId}
          />
          <EditTeamModal
            visible={editTeamModalOpen}
            close={() => setEditTeamModalOpen(false)}
            storeId={storeId}
            hasActiveProductQualityTeam={hasActiveProductQualityTeam}
            selectedTeam={team}
            resetFilters={resetFilters}
            onDelete={() => navigate(teamAdminLink)}
          />
        </>
      )}
    </div>
  );
};

export default TeamManager;
